import React, { useMemo, useState } from "react"

import { FormProvider, useForm } from "react-hook-form"

import { OneVerifyFlagType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types.ts"
import { IOneVerifyReportSavedFilter } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper.ts"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { PoliciesListHelper } from "../../../helpers/policiesList.helper.ts"
import { usePoliciesList } from "../../../hooks/policiesList.hook"
import { PoliciesFiltersFormComponent } from "./policiesFiltersForm.component"
import { PoliciesSavedFiltersComponent } from "./policiesSavedFilters.component"

export const ReportsFiltersComponent: React.FunctionComponent<
    IReportsFiltersComponentProps
> = props => {
    const formMethods = useForm<IPoliciesListFiltersData>()
    const { handleSubmit, watch } = formMethods

    const dataWatcher = watch()

    const { processFiltersFormData } = usePoliciesList()

    const [filtersFormKey, setFiltersFormKey] = useState<number>(
        new Date().getTime(),
    )

    const [selectedFlags, setSelectedFlags] = useState<
        OneVerifyFlagType[] | undefined
    >(props.existingFilters.flags)

    const mappedDataWatcher = useMemo<IPoliciesListFiltersData>(() => {
        // doing JSON functions to remove undefined values
        return JSON.parse(
            JSON.stringify(processFiltersFormData(dataWatcher, selectedFlags)),
        )
    }, [JSON.stringify(dataWatcher), selectedFlags])

    const [selectedSavedFilter, setSelectedSavedFilter] =
        useState<IOneVerifyReportSavedFilter>()

    const [resetFilters, setResetFilters] = useState<IPoliciesListFiltersData>()

    const [isFilterAlreadySaved, setIsFilterAlreadySaved] =
        useState<boolean>(false)

    const [isSavingFilter, setIsSavingFilter] = useState<boolean>(false)

    const onResetFilters = () => {
        setResetFilters(
            PoliciesListHelper.getDefaultFiltersConfiguration(props.listType),
        )
        setSelectedSavedFilter(undefined)
        setSelectedFlags([])
        setFiltersFormKey(new Date().getTime())
    }

    const onSelectSavedFilter = (savedFilter: IOneVerifyReportSavedFilter) => {
        setResetFilters(undefined)
        setSelectedFlags(savedFilter.filtersConfiguration.flags || [])
        setSelectedSavedFilter(savedFilter)

        setTimeout(() => {
            setFiltersFormKey(new Date().getTime())
        })
    }

    const onSubmitForm = (data: IPoliciesListFiltersData) => {
        const mappedData = processFiltersFormData(data, selectedFlags)

        onApplyFilters(mappedData)
    }

    const onApplyFilters = (data: IPoliciesListFiltersData) => {
        BrowserStorageHelper.set(
            props.listType +
                GENERAL_CONFIG.browserStorageKeys
                    .policiesFiltersConfigurationSuffix,
            data,
        )

        props.onFiltersSubmitted(data)
    }

    const onSavedFiltersAsPreset = async () => {
        const filtersValid = await formMethods.trigger()

        if (!filtersValid) {
            return
        }

        setIsSavingFilter(true)
    }

    return (
        <div className={`relative ${props.className}`}>
            <div className="flex h-full">
                <div
                    className={`
                        relative px-[24px] pb-[24px]
                        ${
                            props.withSavingFilterPresets
                                ? "w-[450px] border-r border-solid border-brand-warmLight"
                                : "flex-1"
                        }
                    `}
                >
                    {isSavingFilter && (
                        <div className="absolute left-0 top-0 w-full h-full z-200 bg-brand-offWhite60" />
                    )}

                    <FormProvider {...formMethods}>
                        <form
                            className="h-full"
                            onSubmit={handleSubmit(onSubmitForm)}
                        >
                            <PoliciesFiltersFormComponent
                                key={filtersFormKey}
                                className="h-full overflow-y-auto pb-[100px]"
                                listType={props.listType}
                                existingFilters={
                                    // Note that props.existingFilters should come last
                                    resetFilters ||
                                    selectedSavedFilter?.filtersConfiguration ||
                                    props.existingFilters
                                }
                                selectedFlags={selectedFlags}
                                setSelectedFlags={setSelectedFlags}
                                flagsDisplayedAmount={
                                    !props.withSavingFilterPresets
                                        ? 2
                                        : undefined
                                }
                            />

                            <div
                                className="
                                    absolute bottom-0 z-100 left-0 w-full flex items-center p-[24px]
                                    bg-brand-offWhite border-t border-solid border-brand-warmLight
                                "
                            >
                                <ButtonElement
                                    buttonClassName="min-w-[100px]"
                                    label="Reset"
                                    onClick={onResetFilters}
                                    htmlType="button"
                                    type="default"
                                />

                                <div className="flex-1" />

                                {props.withSavingFilterPresets ? (
                                    <ButtonElement
                                        className="ml-[16px]"
                                        label="Save as Preset"
                                        onClick={onSavedFiltersAsPreset}
                                        htmlType="button"
                                        type="default"
                                        disabled={isFilterAlreadySaved}
                                    />
                                ) : null}

                                <ButtonElement
                                    className="ml-[16px]"
                                    buttonClassName="min-w-[100px]"
                                    label="Filter"
                                    htmlType="submit"
                                    type="primary"
                                />
                            </div>
                        </form>
                    </FormProvider>
                </div>

                {props.withSavingFilterPresets ? (
                    <PoliciesSavedFiltersComponent
                        className="flex-1 h-full overflow-hidden"
                        listType={props.listType}
                        filters={mappedDataWatcher}
                        onSelectSavedFilter={onSelectSavedFilter}
                        onApplySavedFilter={savedFilter => {
                            onApplyFilters(savedFilter.filtersConfiguration)
                        }}
                        onSameSavedFilterFoundChange={setIsFilterAlreadySaved}
                        needSaveCurrentFilters={isSavingFilter}
                        onSaveCurrentFiltersDone={() => {
                            setIsSavingFilter(false)
                        }}
                    />
                ) : null}
            </div>
        </div>
    )
}

interface IReportsFiltersComponentProps {
    className?: string
    listType: PoliciesListType
    existingFilters: IPoliciesListFiltersData
    onFiltersSubmitted: (data: IPoliciesListFiltersData) => void
    withSavingFilterPresets?: boolean
}
